import { useEffect, useRef, useState } from 'react';

export default function GeoHeroSection() {
  const [loaded, setLoaded] = useState(false);
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 100);
    return () => clearTimeout(t);
  }, []);

  const handleScroll = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      ref={heroRef}
      id="geo-hero"
      className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden"
      style={{ background: '#0A0A0A' }}
    >
      {/* Background */}
      <div className="absolute inset-0">
        {/* Deep dark base with subtle radial vignette */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse 80% 60% at 50% 40%, #111111 0%, #0A0A0A 55%, #060606 100%)',
          }}
        />
        {/* Subtle noise texture layer */}
        <div
          className="absolute inset-0 opacity-[0.035]"
          style={{
            backgroundImage:
              'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\'/%3E%3C/svg%3E")',
            backgroundRepeat: 'repeat',
            backgroundSize: '256px 256px',
          }}
        />
      </div>

      {/* Grid */}
      <div className="absolute inset-0 hero-grid opacity-30 pointer-events-none" />

      {/* Red accent blobs */}
      <div className="absolute top-1/3 left-1/3 w-96 h-96 bg-ds-red/6 blur-[130px] rounded-full pointer-events-none" />
      <div className="absolute bottom-1/3 right-1/4 w-80 h-80 bg-ds-blue/5 blur-[100px] rounded-full pointer-events-none" />

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 pt-28 pb-20">
        <div className="max-w-4xl">
          {/* Badge */}
          <div
            className={`inline-flex items-center gap-2 border border-ds-red/40 px-4 py-2 mb-8 transition-all duration-1000 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
            style={{ background: 'rgba(180,255,57,0.08)' }}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-ds-red animate-pulse" />
            <span className="font-body text-ds-red text-xs tracking-[0.3em] uppercase">
              GEO / AEO — Generative Engine Optimisation
            </span>
          </div>

          {/* Headline */}
          <h1
            className={`font-display text-white leading-none mb-6 transition-all duration-1000 delay-200 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ fontSize: 'clamp(3.2rem, 8vw, 8.5rem)', lineHeight: '0.88', letterSpacing: '0.04em' }}
          >
            WHEN YOUR CUSTOMER
            <br />
            ASKS AI WHO TO CALL —
            <br />
            <span className="text-ds-red neon-text-red">WILL THEY</span>
            <br />
            FIND YOU?
          </h1>

          {/* Sub */}
          <p
            className={`font-body text-white/60 text-xl max-w-2xl mt-8 mb-10 leading-relaxed transition-all duration-1000 delay-400 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            GEO and AEO is the new discipline of making sure your business gets recommended by
            ChatGPT, Google Gemini, Perplexity, and every AI search engine that&apos;s replacing
            traditional Google. <strong className="text-white">The future of search is AI. Own it.</strong>
          </p>

          {/* CTAs */}
          <div
            className={`flex flex-wrap items-center gap-4 transition-all duration-1000 delay-600 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            <button
              onClick={() => handleScroll('geo-pricing')}
              className="btn-red text-sm gap-2 hero-cta-glow"
            >
              <span>Get Your Free AI Visibility Audit</span>
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line text-sm" />
              </div>
            </button>
          </div>
        </div>

        {/* Stats bar */}
        <div
          className={`mt-20 max-w-3xl transition-all duration-1000 delay-1000 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          {/* Green accent line */}
          <div className="h-px bg-gradient-to-r from-ds-red via-ds-red/50 to-transparent" />

          <div className="grid grid-cols-2 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-ds-border/60">
            {[
              { num: '800M+', label: 'ChatGPT Weekly Users' },
              { num: '60%', label: 'Searches With AI Overview' },
              { num: '47%', label: 'Brands With Zero GEO Strategy' },
              { num: '10x', label: 'AI Referral Traffic Growth' },
            ].map((s) => (
              <div key={s.label} className="px-6 py-7 text-center md:text-left">
                <div
                  className="font-display text-5xl text-ds-red leading-none mb-2"
                  style={{ letterSpacing: '0.03em' }}
                >
                  {s.num}
                </div>
                <div className="font-body text-white/55 text-xs tracking-[0.2em] uppercase leading-snug">
                  {s.label}
                </div>
              </div>
            ))}
          </div>

          <div className="h-px bg-ds-border/40" />
        </div>
      </div>

      {/* Scroll indicator */}
      <div className={`absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 transition-all duration-1000 delay-1200 ${loaded ? 'opacity-60' : 'opacity-0'}`}>
        <span className="font-body text-white/40 text-xs tracking-widest uppercase">Scroll</span>
        <div className="w-px h-10 bg-gradient-to-b from-white/30 to-transparent" />
      </div>
    </section>
  );
}
